import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { DollarSign, Calendar, Star, Bookmark, CheckCircle, X, Plus, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GREEN = '#00FF41';
const GOLD = '#FFD700';
const CYAN = '#00CCFF';
const RED = '#FF3B30';

const glowText = (color) => ({ color, textShadow: `0 0 6px ${color}66` });
const monoFont = { fontFamily: 'JetBrains Mono, ui-monospace, monospace' };

/**
 * Grant Finder — admin-only panel below the God's Eye Command Center.
 * Uses /api/admin/grants/* endpoints. Pre-seeded on first read.
 */
export default function AdminGrantFinderPanel({ adminKey }) {
  const [stats, setStats] = useState(null);
  const [grants, setGrants] = useState([]);
  const [filter, setFilter] = useState('All');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: '', funder: '', category: 'Small Business', deadline: '', amount_min: 0, amount_max: 0, url: '', description: '' });

  const load = async () => {
    if (!adminKey) return;
    try {
      const [s, g] = await Promise.all([
        axios.get(`${API}/admin/grants/stats?admin_key=${adminKey}`),
        axios.get(`${API}/admin/grants/list?admin_key=${adminKey}${filter !== 'All' ? `&category=${encodeURIComponent(filter)}` : ''}`),
      ]);
      setStats(s.data);
      setGrants(g.data.grants || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, [adminKey, filter]);

  const updateGrant = async (id, body) => {
    try {
      await axios.patch(`${API}/admin/grants/${id}?admin_key=${adminKey}`, body);
      toast.success('Updated');
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const addGrant = async () => {
    if (!form.title || !form.funder) { toast.error('Title and funder required'); return; }
    try {
      await axios.post(`${API}/admin/grants/add?admin_key=${adminKey}`, form);
      toast.success('Grant added');
      setAddOpen(false);
      setForm({ title: '', funder: '', category: 'Small Business', deadline: '', amount_min: 0, amount_max: 0, url: '', description: '' });
      load();
    } catch (e) { toast.error('Add failed'); }
  };

  const daysUntil = (d) => {
    if (!d) return null;
    const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
    return diff;
  };

  const filters = ['All', 'Small Business', 'Hospitality', 'Tech', 'Arts & Music', 'Minority-Owned'];

  return (
    <div className="space-y-3 mt-4" style={monoFont} data-testid="grant-finder-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-mono tracking-widest" style={glowText(GREEN)}>
          ◆ GRANT FINDER · ADMIN ONLY
        </p>
        <button
          onClick={() => setAddOpen(true)}
          data-testid="grant-add-btn"
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded border hover:bg-emerald-900/30 transition"
          style={{ color: GREEN, borderColor: `${GREEN}66` }}
        >
          <Plus className="w-3 h-3" /> Add Grant
        </button>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card className="bg-black/60 border p-2" style={{ borderColor: `${GREEN}44` }}>
          <p className="text-[9px] font-mono tracking-widest" style={glowText(GREEN)}>OPEN GRANTS</p>
          <p className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>{stats?.total_open ?? 0}</p>
        </Card>
        <Card className="bg-black/60 border p-2" style={{ borderColor: `${GOLD}44` }}>
          <p className="text-[9px] font-mono tracking-widest" style={glowText(GOLD)}>$$$ AVAILABLE</p>
          <p className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>${((stats?.total_dollars ?? 0) / 1000).toFixed(0)}K</p>
        </Card>
        <Card className="bg-black/60 border p-2" style={{ borderColor: `${RED}44` }}>
          <p className="text-[9px] font-mono tracking-widest" style={glowText(RED)}>CLOSING THIS WEEK</p>
          <p className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>{stats?.closing_this_week ?? 0}</p>
        </Card>
        <Card className="bg-black/60 border p-2" style={{ borderColor: `${CYAN}44` }}>
          <p className="text-[9px] font-mono tracking-widest" style={glowText(CYAN)}>MATCH SCORE</p>
          <p className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>{stats?.match_score ?? 0}%</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-1 flex-wrap">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`grant-filter-${f.toLowerCase().replace(/[^a-z]/g, '-')}`}
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border transition ${filter === f ? 'bg-emerald-900/50 text-white' : 'text-emerald-300 hover:bg-emerald-900/20'}`}
            style={{ borderColor: filter === f ? GREEN : `${GREEN}44` }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Grants list */}
      <div className="space-y-2">
        {grants.map((g) => {
          const days = daysUntil(g.deadline);
          const urgent = days !== null && days <= 7;
          return (
            <Card
              key={g.id}
              data-testid={`grant-row-${g.id}`}
              className={`bg-black/60 border p-3 ${g.status === 'dismissed' ? 'opacity-40' : ''}`}
              style={{ borderColor: urgent ? RED : g.tracked ? GOLD : `${GREEN}44` }}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-white">{g.title}</p>
                    {g.tracked && <Bookmark className="w-3 h-3" style={{ color: GOLD }} />}
                    {g.status === 'applied' && <CheckCircle className="w-3 h-3" style={{ color: GREEN }} />}
                    {urgent && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: RED, color: 'white' }}>{days}d LEFT</span>}
                  </div>
                  <p className="text-xs text-emerald-300/70 mt-0.5">{g.funder}</p>
                  <p className="text-xs text-zinc-400 mt-1">{g.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] font-mono flex-wrap">
                    <span className="flex items-center gap-1" style={{ color: GOLD }}>
                      <DollarSign className="w-3 h-3" />
                      {g.amount_min ? `$${(g.amount_min / 1000).toFixed(0)}K – $${(g.amount_max / 1000).toFixed(0)}K` : 'Variable'}
                    </span>
                    {g.deadline && (
                      <span className="flex items-center gap-1" style={{ color: CYAN }}>
                        <Calendar className="w-3 h-3" /> {g.deadline}
                      </span>
                    )}
                    <span style={{ color: GREEN }}>{g.category}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <a
                    href={g.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`grant-apply-${g.id}`}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border text-center whitespace-nowrap hover:bg-emerald-900/30"
                    style={{ color: GREEN, borderColor: `${GREEN}66` }}
                  >
                    Apply →
                  </a>
                  <button
                    onClick={() => updateGrant(g.id, { tracked: !g.tracked })}
                    data-testid={`grant-track-${g.id}`}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border hover:bg-yellow-900/30"
                    style={{ color: GOLD, borderColor: `${GOLD}66` }}
                    title={g.tracked ? 'Untrack' : 'Track'}
                  >
                    {g.tracked ? 'Tracked' : 'Track'}
                  </button>
                  <button
                    onClick={() => updateGrant(g.id, { status: g.status === 'applied' ? 'open' : 'applied' })}
                    data-testid={`grant-applied-${g.id}`}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border hover:bg-emerald-900/30"
                    style={{ color: CYAN, borderColor: `${CYAN}66` }}
                  >
                    {g.status === 'applied' ? '✓ Done' : 'Applied'}
                  </button>
                  <button
                    onClick={() => updateGrant(g.id, { status: 'dismissed' })}
                    data-testid={`grant-dismiss-${g.id}`}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border hover:bg-red-900/30"
                    style={{ color: RED, borderColor: `${RED}66` }}
                  >
                    <X className="w-3 h-3 inline" />
                  </button>
                </div>
              </div>
            </Card>
          );
        })}
        {grants.length === 0 && (
          <p className="text-center text-emerald-300/50 py-4 text-sm">No grants in this category</p>
        )}
      </div>

      {/* Add Grant Modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-black border-2 text-white" style={{ borderColor: `${GREEN}66` }} data-testid="grant-add-modal">
          <DialogHeader>
            <DialogTitle style={glowText(GREEN)} className="tracking-widest" data-testid="grant-add-modal-title">ADD NEW GRANT</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black/60 border-emerald-500/40" data-testid="grant-form-title" />
            <Input placeholder="Funder" value={form.funder} onChange={(e) => setForm({ ...form, funder: e.target.value })} className="bg-black/60 border-emerald-500/40" data-testid="grant-form-funder" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Amount Min $" type="number" value={form.amount_min} onChange={(e) => setForm({ ...form, amount_min: Number(e.target.value) })} className="bg-black/60 border-emerald-500/40" />
              <Input placeholder="Amount Max $" type="number" value={form.amount_max} onChange={(e) => setForm({ ...form, amount_max: Number(e.target.value) })} className="bg-black/60 border-emerald-500/40" />
            </div>
            <Input placeholder="Deadline YYYY-MM-DD" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-black/60 border-emerald-500/40" />
            <Input placeholder="URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="bg-black/60 border-emerald-500/40" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-black/60 border border-emerald-500/40 rounded px-2 py-2 text-sm" data-testid="grant-form-category">
              {filters.slice(1).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/60 border-emerald-500/40" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addGrant} className="bg-emerald-600 hover:bg-emerald-700" data-testid="grant-add-submit">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
